import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import CabinetColumnCustomizerDraggableRow, { StaticRow, ToggleRow } from './cabinetColumnCustomizerDraggableRow';
import { matchesSearchTerm } from '../../utils/cabinetColumnUtils';
const reorderList = (list, fromIndex, toIndex) => {
  const next = [...list];
  const [removed] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, removed);
  return next;
};
const CabinetColumnCustomizer = ({
  isOpen,
  staticColumns,
  candidateFields,
  addedColumnIndexes,
  onSave,
  onClose
}) => {
  const [pendingIndexes, setPendingIndexes] = useState(addedColumnIndexes);
  const [searchTerm, setSearchTerm] = useState('');
  useEffect(() => {
    if (isOpen) {
      setPendingIndexes(addedColumnIndexes);
      setSearchTerm('');
    }
  }, [isOpen, addedColumnIndexes]);
  const fieldByKey = useMemo(() => {
    const map = {};
    candidateFields.forEach(field => {
      map[field.key] = field;
    });
    return map;
  }, [candidateFields]);
  const hiddenFields = useMemo(() => candidateFields.filter(f => !pendingIndexes.includes(f.key)), [candidateFields, pendingIndexes]);
  const filteredStaticColumns = useMemo(() => staticColumns.filter(col => matchesSearchTerm(col.title || '', searchTerm)), [staticColumns, searchTerm]);
  const filteredPendingKeys = useMemo(() => pendingIndexes.filter(key => {
    var _fieldByKey$key;
    const title = ((_fieldByKey$key = fieldByKey[key]) === null || _fieldByKey$key === void 0 ? void 0 : _fieldByKey$key.title) || key;
    return matchesSearchTerm(title, searchTerm);
  }), [pendingIndexes, fieldByKey, searchTerm]);
  const filteredHiddenFields = useMemo(() => hiddenFields.filter(f => matchesSearchTerm(f.title, searchTerm)), [hiddenFields, searchTerm]);
  const handleToggleOn = key => {
    setPendingIndexes(prev => prev.includes(key) ? prev : [...prev, key]);
  };
  const handleToggleOff = key => {
    setPendingIndexes(prev => prev.filter(k => k !== key));
  };
  const handleHideAll = () => {
    setPendingIndexes([]);
  };
  const handleOrderChange = (fromIndex, toIndex) => {
    setPendingIndexes(prev => reorderList(prev, fromIndex, toIndex));
  };
  const handleSave = () => {
    onSave(pendingIndexes);
  };
  const handleClose = () => {
    setPendingIndexes(addedColumnIndexes);
    setSearchTerm('');
    onClose();
  };
  const shownCustomCount = pendingIndexes.length;
  const hasShownSection = filteredStaticColumns.length > 0 || filteredPendingKeys.length > 0;
  const hasHiddenSection = filteredHiddenFields.length > 0;
  return /*#__PURE__*/React.createElement(Modal, {
    isOpen: isOpen,
    toggle: handleClose,
    size: "sm",
    className: "t-col-customizer-modal",
    "data-testid": "t-col-customizer-modal"
  }, /*#__PURE__*/React.createElement(ModalHeader, {
    toggle: handleClose,
    className: "border-0 pb-0"
  }, "Fields"), /*#__PURE__*/React.createElement(ModalBody, {
    className: "pt-2 pb-0"
  }, /*#__PURE__*/React.createElement(Input, {
    type: "search",
    placeholder: "Search for fields",
    value: searchTerm,
    onChange: e => setSearchTerm(e.target.value),
    "data-testid": "t-col-customizer-search",
    className: "mb-3"
  }), /*#__PURE__*/React.createElement("div", {
    className: "t-col-customizer-scroll",
    style: {
      maxHeight: 'min(60vh, 420px)',
      overflowY: 'auto'
    }
  }, hasShownSection && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center justify-content-between px-3 mb-1"
  }, /*#__PURE__*/React.createElement("span", {
    className: "small fw-semibold text-muted text-uppercase"
  }, "Shown"), shownCustomCount > 0 && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "btn btn-link btn-sm p-0 text-decoration-none",
    "data-testid": "t-col-customizer-hide-all",
    onClick: handleHideAll
  }, "Hide all")), filteredStaticColumns.map(col => /*#__PURE__*/React.createElement(StaticRow, {
    key: col.title,
    testIdPrefix: "t-col-customizer",
    title: col.title
  })), filteredPendingKeys.map(key => {
    var _fieldByKey$key2;
    const fullIndex = pendingIndexes.indexOf(key);
    const title = ((_fieldByKey$key2 = fieldByKey[key]) === null || _fieldByKey$key2 === void 0 ? void 0 : _fieldByKey$key2.title) || key;
    return /*#__PURE__*/React.createElement(CabinetColumnCustomizerDraggableRow, {
      key: key,
      fieldKey: key,
      title: title,
      index: fullIndex,
      onOrderChange: handleOrderChange,
      onToggleOff: handleToggleOff
    });
  })), hasShownSection && hasHiddenSection && /*#__PURE__*/React.createElement("hr", {
    className: "my-2"
  }), hasHiddenSection && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "px-3 mb-1"
  }, /*#__PURE__*/React.createElement("span", {
    className: "small fw-semibold text-muted text-uppercase"
  }, "Hidden")), filteredHiddenFields.map(({
    key,
    title
  }) => /*#__PURE__*/React.createElement(ToggleRow, {
    key: key,
    testIdPrefix: "t-col-customizer-hidden",
    itemKey: key,
    title: title,
    checked: false,
    onToggle: handleToggleOn
  }))), !hasShownSection && !hasHiddenSection && /*#__PURE__*/React.createElement("p", {
    className: "text-muted small px-3 mb-0"
  }, "No fields match your search."))), /*#__PURE__*/React.createElement(ModalFooter, {
    className: "border-0 pt-2"
  }, /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    "data-testid": "t-col-customizer-done",
    onClick: handleSave
  }, "Done")));
};
CabinetColumnCustomizer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  staticColumns: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired
  })).isRequired,
  candidateFields: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
  })).isRequired,
  addedColumnIndexes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};
export default CabinetColumnCustomizer;